import type { ISketchObject } from '../../../../mesh/sketch';
import {
	isSerializablePlainData,
	type SerializablePlainData,
} from '../../../../history/domain/SerializablePlainData';
import { SketchRelationKind } from '../relation-compatibility/SketchRelationKinds';
import { createSketchConstraintPersistentId } from '../../../identity/SketchPersistentIdFactory';

let runtimeConstraintCount = 0;

/**
 * 判断对象是否为草图关系定义实例。
 *
 * @param value 待判断对象。
 * @returns 对象是否具有草图关系定义标记。
 */
export function isSketchRelationDefinition(value: object): value is SketchRelationDefinition<any> {
	return Reflect.get(value, 'isSketchRelationDefinition') === true;
}

/**
 * 草图关系定义抽象基类。
 *
 * 历史类名暂时保留以控制持久化和调用方迁移范围，但本类不再提供任何求解模板。
 * 它只负责关系身份、目标绑定、诊断状态和参数公式载荷；几何计算由 `sketch/solver`
 * 中的公式编译器与统一协调器完成。
 */
export abstract class SketchRelationDefinition<TTarget extends ISketchObject = ISketchObject> {
	/** 跨包判断草图关系定义的稳定运行时标记。 */
	public readonly isSketchRelationDefinition = true;
	/** 当前会话内的关系标识。 */
	public id = 0;
	/** 关系实例显示名称。 */
	public name = '';
	/**
	 * 跨会话稳定持久标识。
	 *
	 * 克隆历史快照时应继承该标识；复制为新关系时必须显式生成新标识。
	 */
	public persistentId: string = createSketchConstraintPersistentId();
	/** 标记该关系是否只用于展示派生诊断。 */
	public readonly isTransientDiagnosticConstraint: boolean = false;
	/** 是否禁止用户按普通几何关系单独删除。 */
	public readonly isReadOnly: boolean = false;
	/** 当前关系是否因外部引用失效而悬空。 */
	public isDangling = false;
	/** 当前关系是否为转换实体引用失败诊断。 */
	public isFailed = false;
	/** 当前关系是否被用户抑制；抑制关系保留定义但不进入活动方程组。 */
	public isSuppressed = false;
	/** 悬空关系的可定位诊断消息。 */
	public danglingDiagnosticMessage?: string;
	/** 失败关系的可定位诊断消息。 */
	public failureDiagnosticMessage?: string;
	/** 关系的领域类型。 */
	public abstract relationType: SketchRelationKind;
	/** 当前稳定多解分支提示；只允许可序列化 plain data。 */
	private branchHint: SerializablePlainData | null = null;
	/** 与当前关系绑定的目标对象集合。 */
	protected readonly targets = new Set<TTarget>();

	/** 创建当前会话内唯一的关系标识。 */
	public constructor() {
		this.id = runtimeConstraintCount;
		runtimeConstraintCount += 1;
	}

	/** @returns 关系面板使用的基础显示名。 */
	public get baseDisplayName(): string {
		return '';
	}

	/**
	 * 绑定一个关系目标。
	 *
	 * @param target 需要参与参数公式编译的草图对象。
	 */
	public bindTarget(target: TTarget): void {
		this.targets.add(target);
	}

	/**
	 * 批量绑定关系目标。
	 *
	 * @param targets 需要参与参数公式编译的草图对象集合。
	 */
	public bindTargets(targets: Iterable<TTarget>): void {
		for (const target of targets) {
			this.targets.add(target);
		}
	}

	/**
	 * 将关系标记为悬空并保留诊断原因。
	 *
	 * @param diagnosticMessage 外部引用领域状态投影出的诊断消息。
	 */
	public markDangling(diagnosticMessage: string): void {
		this.isDangling = true;
		this.danglingDiagnosticMessage = diagnosticMessage;
	}

	/** 清除关系的派生悬空状态。 */
	public clearDangling(): void {
		this.isDangling = false;
		this.danglingDiagnosticMessage = undefined;
	}

	/** 将关系标记为失败诊断并保留原因。 */
	public markFailed(diagnosticMessage: string): void {
		this.isFailed = true;
		this.failureDiagnosticMessage = diagnosticMessage;
	}

	/** 清除关系的派生失败状态。 */
	public clearFailed(): void {
		this.isFailed = false;
		this.failureDiagnosticMessage = undefined;
	}

	/**
	 * 设置关系抑制状态。
	 *
	 * 本方法只修改领域状态；调用方必须在同一历史事务中重新求解完整参数岛，
	 * 禁止关系对象直接修改几何。
	 *
	 * @param suppressed 是否抑制。
	 */
	public setSuppressed(suppressed: boolean): void {
		this.isSuppressed = suppressed;
	}

	/**
	 * 读取当前稳定多解分支提示。
	 *
	 * @returns 深拷贝后的分支提示；未建立稳定分支时返回 null。
	 */
	public getBranchHint(): SerializablePlainData | null {
		return this.branchHint === null
			? null
			: JSON.parse(JSON.stringify(this.branchHint)) as SerializablePlainData;
	}

	/**
	 * 写入求解器确认的稳定多解分支提示。
	 *
	 * @param branchHint 可序列化分支数据；null 表示清除。
	 */
	public setBranchHint(branchHint: SerializablePlainData | null): void {
		if (branchHint !== null && !isSerializablePlainData(branchHint)) {
			throw new Error(`关系 ${this.persistentId} 的分支提示必须是有限、可序列化的 plain data。`);
		}
		this.branchHint = branchHint === null
			? null
			: JSON.parse(JSON.stringify(branchHint)) as SerializablePlainData;
	}

	/**
	 * 获取当前已绑定目标的只读快照。
	 *
	 * @returns 当前目标集合的数组副本。
	 */
	public getTargetsSnapshot(): readonly TTarget[] {
		return [...this.targets];
	}

	/**
	 * 获取默认关系目标。
	 *
	 * @returns 目标集合中的第一个对象；尚未绑定时返回 `null`。
	 */
	protected getTargetOrThrow(): TTarget | null {
		const firstTarget = this.targets.values().next().value as TTarget | undefined;
		if (typeof firstTarget === 'undefined') {
			console.error(`约束 ${this.id} 尚未绑定目标对象。`);
			return null;
		}
		return firstTarget;
	}

	/**
	 * 克隆带参数的关系定义。
	 *
	 * 无参关系由通用克隆器构造；持有可变载荷的关系必须覆写并执行深拷贝。
	 *
	 * @returns 等价的新关系实例；返回 `null` 表示可使用无参构造。
	 */
	public cloneSelf(): SketchRelationDefinition<TTarget> | null {
		return null;
	}

	/**
	 * 创建参数公式编译所需的额外纯数据载荷。
	 *
	 * 普通几何关系只依赖关系类型与目标；外部引用关系通过本方法公开局部坐标边界。
	 *
	 * @returns 可序列化公式载荷；无需额外数据时返回 `null`。
	 */
	public createParametricFormulaPayload(): SerializablePlainData | null {
		return null;
	}
}
